import { handleError } from "../../../util/errors";
import apiBase from "../../api";
import fileDownload from "js-file-download";
import {
  BILLING_INVOICES,
  BILLING_INVOICE,
  CREATE_BILLING_INVOICE,
  UPDATE_BILLING_INVOICE,
  UPDATE_BILLING_INVOICE_REFERENCE,
  SEND_BILLING_INVOICE,
  CANCEL_BILLING_INVOICE,
  TOGGLE_BILLING_INVOICE_PAID,
  GENERATE_ALL_BILLING_INVOICES,
  BILLING_INVOICE_ITEMS,
  CREATE_BILLING_INVOICE_ITEM,
  UPDATE_BILLING_INVOICE_ITEM,
  DELETE_BILLING_INVOICE_ITEM,
  BILLING_INVOICE_PAYMENTS,
  CREATE_BILLING_INVOICE_PAYMENT,
  UPDATE_BILLING_INVOICE_PAYMENT,
  DELETE_BILLING_INVOICE_PAYMENT,
  FETCH_INVOICE_SCHEMAS,
  FETCH_INVOICE_SCHEMA,
  CREATE_INVOICE_SCHEMA,
  UPDATE_INVOICE_SCHEMA,
  DELETE_INVOICE_SCHEMA,
  FETCH_BILLING_COMPANIES,
  CREATE_BILLING_COMPANY,
  UPDATE_BILLING_COMPANY,
  DELETE_BILLING_COMPANY,
  GENERATE_INVOICE_FROM_SCHEMA,
  FETCH_INVOICE_SCHEMA_IDS,
  BULK_CREATE_BILLING_INVOICES,
  EXPORT_BILLING_INVOICES,
} from "../../urls";

class BillingInvoicesService {
  getBillingInvoices = async (page = 1, perPage = 20, companyId = null, status = null, searchTerm = "", billingPeriodStart = null, billingPeriodEnd = null, isPaid = null) => {
    try {
      const response = await apiBase.get(BILLING_INVOICES(page, perPage, companyId, status, searchTerm, billingPeriodStart, billingPeriodEnd, isPaid));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  getBillingInvoice = async (id) => {
    try {
      const response = await apiBase.get(BILLING_INVOICE(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  createBillingInvoice = async (invoiceData) => {
    try {
      const response = await apiBase.post(CREATE_BILLING_INVOICE, invoiceData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  updateBillingInvoice = async (id, invoiceData) => {
    try {
      const response = await apiBase.put(UPDATE_BILLING_INVOICE(id), invoiceData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  updateBillingInvoiceReference = async (id, reference) => {
    try {
      const response = await apiBase.put(UPDATE_BILLING_INVOICE_REFERENCE(id), { reference });
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  sendBillingInvoice = async (id) => {
    try {
      const response = await apiBase.post(SEND_BILLING_INVOICE(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  cancelBillingInvoice = async (id) => {
    try {
      const response = await apiBase.post(CANCEL_BILLING_INVOICE(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  togglePaidBillingInvoice = async (id) => {
    try {
      const response = await apiBase.post(TOGGLE_BILLING_INVOICE_PAID(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  generateAllBillingInvoices = async () => {
    try {
      const response = await apiBase.post(GENERATE_ALL_BILLING_INVOICES);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  // Invoice Items
  getInvoiceItems = async (id) => {
    try {
      const response = await apiBase.get(BILLING_INVOICE_ITEMS(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  createInvoiceItem = async (id, itemData) => {
    try {
      const response = await apiBase.post(CREATE_BILLING_INVOICE_ITEM(id), itemData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  updateInvoiceItem = async (id, itemId, itemData) => {
    try {
      const response = await apiBase.put(UPDATE_BILLING_INVOICE_ITEM(id, itemId), itemData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  deleteInvoiceItem = async (id, itemId) => {
    try {
      const response = await apiBase.delete(DELETE_BILLING_INVOICE_ITEM(id, itemId));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  // Invoice Payments
  getInvoicePayments = async (id) => {
    try {
      const response = await apiBase.get(BILLING_INVOICE_PAYMENTS(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  createInvoicePayment = async (id, paymentData) => {
    try {
      const response = await apiBase.post(CREATE_BILLING_INVOICE_PAYMENT(id), paymentData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  updateInvoicePayment = async (id, paymentId, paymentData) => {
    try {
      const response = await apiBase.put(UPDATE_BILLING_INVOICE_PAYMENT(id, paymentId), paymentData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  deleteInvoicePayment = async (id, paymentId) => {
    try {
      const response = await apiBase.delete(DELETE_BILLING_INVOICE_PAYMENT(id, paymentId));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  getInvoiceSchemas = async (page = 1, perPage = 20, sortBy = "id", order = "DESC", searchTerm = "") => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
        sortBy: sortBy,
        order: order,
      });
      if (searchTerm) {
        params.append("search", searchTerm);
      }
      const response = await apiBase.get(`${FETCH_INVOICE_SCHEMAS}?${params.toString()}`);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  getInvoiceSchema = async (id) => {
    try {
      const response = await apiBase.get(FETCH_INVOICE_SCHEMA(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  getBillingCompanies = async (page = 1, perPage = 20, searchTerm = "") => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
      });
      if (searchTerm) {
        params.append("search", searchTerm);
      }
      const response = await apiBase.get(`${FETCH_BILLING_COMPANIES}?${params.toString()}`);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  generateInvoiceFromSchema = async (schemaId, periodData = {}) => {
    try {
      const response = await apiBase.post(GENERATE_INVOICE_FROM_SCHEMA(schemaId), periodData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  // Invoice Schemas
  createInvoiceSchema = async (schemaData) => {
    try {
      const response = await apiBase.post(CREATE_INVOICE_SCHEMA, schemaData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  updateInvoiceSchema = async (id, schemaData) => {
    try {
      const response = await apiBase.put(UPDATE_INVOICE_SCHEMA(id), schemaData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  deleteInvoiceSchema = async (id) => {
    try {
      const response = await apiBase.delete(DELETE_INVOICE_SCHEMA(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  // Billing Companies
  createBillingCompany = async (companyData) => {
    try {
      const response = await apiBase.post(CREATE_BILLING_COMPANY, companyData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  updateBillingCompany = async (id, companyData) => {
    try {
      const response = await apiBase.put(UPDATE_BILLING_COMPANY(id), companyData);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  deleteBillingCompany = async (id) => {
    try {
      const response = await apiBase.delete(DELETE_BILLING_COMPANY(id));
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  getInvoiceSchemaIds = async (isActive = null) => {
    try {
      let url = FETCH_INVOICE_SCHEMA_IDS;
      if (isActive !== null && isActive !== undefined) {
        // API expects string "true" or "false", not boolean
        const isActiveStr = isActive === true ? "true" : isActive === false ? "false" : String(isActive);
        url = `${FETCH_INVOICE_SCHEMA_IDS}?isActive=${isActiveStr}`;
      }
      const response = await apiBase.get(url);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  bulkCreateBillingInvoices = async (bulkData) => {
    try {
      console.log("bulkCreateBillingInvoices service called with:", JSON.stringify(bulkData, null, 2));
      console.log("Making POST request to:", BULK_CREATE_BILLING_INVOICES);
      
      // Explicitly ensure method is POST
      const response = await apiBase.post(BULK_CREATE_BILLING_INVOICES, bulkData, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log("bulkCreateBillingInvoices response:", response);
      return response.data;
    } catch (error) {
      console.error("bulkCreateBillingInvoices error:", error);
      console.error("Error config:", error.config);
      handleError(error);
      throw error;
    }
  };

  exportBillingInvoices = async (filters = {}) => {
    try {
      const { billingPeriodStart, billingPeriodEnd, companyId, status, isPaid, format = 'xlsx' } = filters;
      
      // Convert status array to format expected by URL builder
      let statusParam = null;
      if (status) {
        if (Array.isArray(status)) {
          statusParam = status;
        } else if (typeof status === 'string') {
          statusParam = status;
        }
      }

      const url = EXPORT_BILLING_INVOICES(
        billingPeriodStart,
        billingPeriodEnd,
        companyId,
        statusParam,
        isPaid,
        format
      );

      const response = await apiBase.get(url, {
        responseType: 'blob',
      });

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'Billing_Invoices.xlsx';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      fileDownload(response.data, fileName);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };
}

export default new BillingInvoicesService();

